import React, { useEffect, useState } from "react";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import { MaterialReactTable } from "material-react-table";
import { Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import userResources from "api/userResources";
import { selectUserInfo } from "../../../redux/userSlice";
import { useSelector } from "react-redux";
import { FaMailBulk, FaWhatsapp } from "react-icons/fa";
import Modal from "components/daisyUiMsgModal/Modal";

export default function InstaEnquiry() {
  const userInfo = useSelector(selectUserInfo);
  const [globalFilter, setGlobalFilter] = useState("");
  const [enquiryData, setEnquiryData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility

  const fetchInstagramEnquiries = async (pageIndex, pageSize, globalFilter) => {
    setIsLoading(true);
    try {
      const response = await userResources.instaEnquiryList(
        pageIndex || 0,
        pageSize || 5,
        globalFilter || ""
      );
      setEnquiryData(response.data.data);
      setTotalRows(response.data.paginate_data.total);
    } catch (error) {
      console.error("Error fetching Instagram Enquiries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    console.log(id);

    const loadingToastId = toast.loading("Deleting Instagram Enquiry...");
    try {
      await userResources.instaEnquiryDelete(id);
      toast.update(loadingToastId, {
        render: "Instagram Enquiry deleted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      fetchInstagramEnquiries(
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter
      );
    } catch (error) {
      console.error("Error deleting Instagram Enquiry:", error);
      toast.update(loadingToastId, {
        render: "Failed to delete Instagram Enquiry. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      if (toast.isActive(loadingToastId)) {
        toast.dismiss(loadingToastId);
      }
    }
  };

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
  };

  useEffect(() => {
    fetchInstagramEnquiries(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter
    );
  }, [pagination, globalFilter]);

  const handlePaginationChange = (updatedPagination) => {
    setPagination(updatedPagination);
  };

  // Function to format date and time separately
  const formatDateAndTime = (timestamp) => {
    const date = new Date(timestamp);

    // Extract date in YYYY-MM-DD format
    const formattedDate = date.toISOString().split("T")[0];

    // Extract time in HH:MM:SS format
    const formattedTime = date.toTimeString().split(" ")[0];

    return { formattedDate, formattedTime };
  };

  const handleModalOpen = (message) => {
    setSelectedMessage(message); // Set the travel experience text
    setIsModalOpen(true); // Open the modal
  };

  const handleModalClose = () => {
    setIsModalOpen(false); // Close the modal
    setSelectedMessage(""); // Reset the travel experience
  };

  const columns = [
    { accessorKey: "id", header: "Id" },
    { accessorKey: "full_name", header: "Name" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phone", header: "Phone Number" },
    { accessorKey: "departure_city", header: "Departure City" },
    { accessorKey: "destination_city", header: "Destination City" },
    { accessorKey: "no_of_passengers", header: "Passengers" },
    {
      accessorKey: "message",
      header: "Message",
      Cell: ({ row }) => (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleModalOpen(row.original.message)}
        >
          Show
        </button>
      ),
    },
    {
      accessorKey: "travel_date",
      header: "Travel date",
      Cell: ({ cell }) => {
        const date = new Date(cell.getValue());
        return <span>{date.toISOString().split("T")[0]}</span>;
      },
    },
    {
      accessorKey: "return_date",
      header: "Return date",
      Cell: ({ cell }) => {
        const date = new Date(cell.getValue());
        return <span>{date.toISOString().split("T")[0]}</span>;
      },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      Cell: ({ cell }) => {
        const { formattedDate, formattedTime } = formatDateAndTime(
          cell.getValue()
        );

        return (
          <div>
            <div>Date: {formattedDate}</div>
            <div>Time: {formattedTime}</div>
          </div>
        );
      },
    },
    {
      accessorKey: "action",
      header: "Action",
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          {userInfo?.data?.role_id !==
            Number(process.env.REACT_APP_ROLE_EMPLOYEE) && (
            <ConfirmModal
              onConfirm={handleDelete}
              title="Delete Instagram Enquiry"
              message="Are you sure you want to delete this Instagram Enquiry?"
              employeeId={row.original.id}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <section className="mt-6">
      <ScrollToTopOnRouteChange />
      <MaterialReactTable
        columns={columns}
        data={enquiryData}
        state={{ pagination, isLoading }}
        enablePagination
        onPaginationChange={handlePaginationChange}
        manualPagination
        rowCount={totalRows}
        pageCount={Math.ceil(totalRows / pagination.pageSize)}
        isLoading={isLoading}
        enableColumnFilters={false}
        enableSorting={false}
        enableColumnOrdering={false}
        muiTablePaginationProps={{
          showFirstButton: true,
          showLastButton: true,
        }}
        enableGlobalFilter
        onGlobalFilterChange={handleGlobalFilterChange}
        globalFilter={globalFilter}
        enableRowActions
        renderRowActions={({ row }) => (
          <div className="flex flex-nowrap gap-4">
            <button
              className="text-xl text-primary"
              onClick={() => {
                const { formattedDate, formattedTime } = formatDateAndTime(
                  row.original.created_at
                );

                window.open(
                  `mailto:${row.original.email}?subject=Travel Enquiry Details&body=Dear ${row.original.full_name},%0D%0A%0D%0A
Thank you for submitting your travel enquiry with Kataria Tours and Travel! Here are the details of your request:%0D%0A%0D%0A
- **Name**: ${row.original.full_name}%0D%0A
- **Email**: ${row.original.email}%0D%0A
- **Phone Number**: ${row.original.phone}%0D%0A
- **Departure City**: ${row.original.departure_city}%0D%0A
- **Destination City**: ${row.original.destination_city}%0D%0A
- **Number of Passengers**: ${row.original.no_of_passengers}%0D%0A
- **Travel Date**: ${row.original.travel_date}%0D%0A
- **Return Date**: ${row.original.return_date}%0D%0A
- **Enquiry Created On (Date)**: ${formattedDate}%0D%0A
- **Enquiry Created On (Time)**: ${formattedTime}%0D%0A%0D%0A
We are reviewing your request and will get back to you shortly. Should you have any additional questions, feel free to contact us directly here.%0D%0A%0D%0A
Best Regards,%0D%0A
The Team at Kataria Tours and Travel%0D%0A%0D%0A
---%0D%0A
**Company Contact Information**:%0D%0A
- **Website**: https://www.katariatravel.com%0D%0A
- **Toll Free Number**: +1 1888-678-1201%0D%0A
- **Email**: katariatravel@gmail.com%0D%0A
- **Address**: UNIT 12, 4550 EBENEZER RD BRAMPTON ONTARIO L6P 1H4, Canada%0D%0A`
                );
              }}
              title="Send Email"
            >
              <FaMailBulk />
            </button>

            <button
              className="text-xl text-success"
              onClick={() => {
                const { formattedDate, formattedTime } = formatDateAndTime(
                  row.original.created_at
                );
                window.open(
                  `https://wa.me/${row.original.phone.replace(
                    /\D/g,
                    ""
                  )}?text=Hello ${row.original.full_name},%0D%0A%0D%0A
Thank you for submitting your travel enquiry with Kataria Tours and Travel! Here are the details of your request:%0D%0A%0D%0A
- *Name*: ${row.original.full_name}%0D%0A
- *Email*: ${row.original.email}%0D%0A
- *Phone Number*: ${row.original.phone}%0D%0A
- *Departure City*: ${row.original.departure_city}%0D%0A
- *Destination City*: ${row.original.destination_city}%0D%0A
- *Number of Passengers*: ${row.original.no_of_passengers}%0D%0A
- *Travel Date*: ${row.original.travel_date}%0D%0A
- *Return Date*: ${row.original.return_date}%0D%0A
- **Enquiry Created On (Date)**: ${formattedDate}%0D%0A
- **Enquiry Created On (Time)**: ${formattedTime}%0D%0A%0D%0A
We are reviewing your request and will get back to you shortly. Should you have any additional questions, feel free to contact us directly here.%0D%0A%0D%0A
Best Regards,%0D%0A
 The Team at Kataria Tours and Travel`
                );
              }}
            >
              <FaWhatsapp />
            </button>
          </div>
        )}
      />
      {isModalOpen && (
        <Modal
          heading="Message"
          paragraph={selectedMessage}
          close={handleModalClose}
        />
      )}
    </section>
  );
}
